import React from 'react';

import { Button, Loader, Modal, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PasswordInputWithStrength } from 'components/PasswordInputWithValidation';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AuthService } from 'services/auth-service';
import { NotificationService } from 'services/notification-service';
import { UserService } from 'services/user-service';
import { USER_INFO_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { confirmPasswordValidator, requiredInputValidator } from 'shared/utils/form-validators';

interface ResetTemporaryPasswordModalProps {
  opened: boolean;
  email: string;
  temporaryPassword: string;
  onClose: () => void;
}

export function ResetTemporaryPasswordModal({
  opened,
  email,
  temporaryPassword,
  onClose
}: ResetTemporaryPasswordModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const passwordForm = useForm({
    initialValues: { password: '', confirmPassword: '' },
    validate: {
      password: (value) => requiredInputValidator(value),
      confirmPassword: (value, values) =>
        requiredInputValidator(value) || confirmPasswordValidator(values.password, value)
    }
  });

  const resetTemporaryPasswordMutation = useMutation({
    mutationFn: () =>
      AuthService.changeTemporaryPassword({
        email: email,
        password: temporaryPassword,
        newPassword: passwordForm.values.password
      }),
    onSuccess: async () => {
      onClose();
      userInfoQuery.refetch();
      NotificationService.showResult(true, t('AUTH.RESET_PASSWORD.SUCCESS'));
    },
    onError: () => NotificationService.showResult(false, t('AUTH.RESET_PASSWORD.ERROR'))
  });

  const userInfoQuery = useQuery({
    queryKey: [USER_INFO_QUERY_KEY],
    queryFn: () => UserService.userInfo(),
    onSuccess() {
      navigate(ROUTE_LINKS.dashboard);
    },
    onError: () => {
      UserService.logout();
      NotificationService.showResult(false, t('AUTH.ERROR.SIGN_IN'));
    },
    enabled: false
  });

  const handleResetTemporaryPassword = () => {
    const isInvalid = passwordForm.validate().hasErrors;
    if (isInvalid) return;
    resetTemporaryPasswordMutation.mutate();
  };

  return (
    <>
      <Modal
        opened={opened}
        title={t('AUTH.TEMPORARY_PASSWORD.LABEL')}
        onClose={() => onClose()}
        styles={{
          inner: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh'
          }
        }}>
        <Text size="sm">{t('AUTH.TEMPORARY_PASSWORD.HINT')}</Text>
        <form onSubmit={passwordForm.onSubmit(() => handleResetTemporaryPassword())}>
          <PasswordInputWithStrength
            passwordInputProps={passwordForm.getInputProps('password')}
            confirmPasswordInputProps={passwordForm.getInputProps('confirmPassword')}
          />
          <Button fullWidth type="submit" mt="xl" disabled={resetTemporaryPasswordMutation.isLoading}>
            {resetTemporaryPasswordMutation.isLoading ? (
              <Loader size="sm" />
            ) : (
              t('AUTH.TEMPORARY_PASSWORD.UPDATE_PASSWORD_BUTTON_LABEL')
            )}
          </Button>
        </form>
      </Modal>
    </>
  );
}
